import { useEffect, useReducer } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AUTH_REDIRECT_PARAM, fetchCurrentUser, UnauthenticatedError, type CurrentUser } from '../../api/authenticationApi'
import { ROUTES } from '../../routes'

export const AuthStatuses = {
  authFailed: 'AUTH_FAILED',
  authInProgress: 'AUTH_IN_PROGRESS',
  authLoginRequired: 'AUTH_LOGIN_REQUIRED',
  authSignedIn: 'AUTH_SIGNED_IN',
  authStart: 'AUTH_START',
} as const

export type AuthStatus = (typeof AuthStatuses)[keyof typeof AuthStatuses]

export interface AuthState {
  readonly redirectAfterLoginUrl: string | null
  readonly status: AuthStatus
  readonly user: CurrentUser | null
}

type AuthAction =
  | { type: 'AUTHENTICATING' }
  | { type: 'AUTHENTICATION_FAILED' }
  | { type: 'AUTHENTICATION_SUCCEEDED'; user: CurrentUser }
  | { type: 'LOGIN_REQUIRED'; redirectAfterLoginUrl: string | null }

const initialState: AuthState = {
  redirectAfterLoginUrl: null,
  status: AuthStatuses.authStart,
  user: null,
}

// ponytail: local/testing-only bypass so the app shell can be exercised without a live Authentication
// API session. Gated behind an explicit opt-in env flag (unset/false by default) so it can never be
// active unless a developer deliberately enables it in their own untracked .env file.
const isAuthBypassEnabled = import.meta.env.VITE_BYPASS_AUTH === 'true'

const bypassUser: CurrentUser = {
  id: 'local-test-user',
  fullName: 'Test User',
  initials: 'TU',
  email: 'test.user@bmw.de',
}

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTHENTICATING':
      return { ...state, status: AuthStatuses.authInProgress }
    case 'AUTHENTICATION_SUCCEEDED':
      return { ...state, status: AuthStatuses.authSignedIn, user: action.user }
    case 'AUTHENTICATION_FAILED':
      return { ...state, status: AuthStatuses.authFailed, user: null }
    case 'LOGIN_REQUIRED':
      return { redirectAfterLoginUrl: action.redirectAfterLoginUrl, status: AuthStatuses.authLoginRequired, user: null }
    default:
      return state
  }
}

/**
 * Drives the authentication state machine: on mount it checks for an existing session
 * (or completes the redirect back from the identity provider), and otherwise remembers
 * the originally requested path so Router can send the user back there after login.
 */
export function useAuthStatus(): AuthState {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (state.status !== AuthStatuses.authStart) {
      return
    }

    if (isAuthBypassEnabled) {
      dispatch({ type: 'AUTHENTICATION_SUCCEEDED', user: bypassUser })
      return
    }

    const isRedirectFromAuth = new URLSearchParams(location.search).get(AUTH_REDIRECT_PARAM) === 'true'
    const requestedUrl = `${location.pathname}${location.search}`

    if (isRedirectFromAuth) {
      navigate(location.pathname, { replace: true })
    }

    dispatch({ type: 'AUTHENTICATING' })

    fetchCurrentUser()
      .then((user) => dispatch({ type: 'AUTHENTICATION_SUCCEEDED', user }))
      .catch((error: unknown) => {
        if (!(error instanceof UnauthenticatedError)) {
          dispatch({ type: 'AUTHENTICATION_FAILED' })
          return
        }

        const isOnLoginRoute = location.pathname === ROUTES.login
        dispatch({ type: 'LOGIN_REQUIRED', redirectAfterLoginUrl: isOnLoginRoute ? null : requestedUrl })
      })
  }, [location, navigate, state.status])

  return state
}
